import React, { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const MembersManager = ({ studyCircleId, members: initialMembers, onMembersUpdate }) => {
    const { user } = useAuth();
    const [members, setMembers] = useState(initialMembers);
    const [showAddStudentModal, setShowAddStudentModal] = useState(false);
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [studentSearch, setStudentSearch] = useState('');
    const [studentSearchType, setStudentSearchType] = useState('username');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isAddingStudent, setIsAddingStudent] = useState(false);
    const [isChangingPassword, setIsChangingPassword] = useState(false);

    const handleRemoveMember = async (memberId) => {
        if (!window.confirm('Are you sure you want to remove this member?')) {
            return;
        }

        setError('');
        setSuccess('');
        try {
            await api.delete(`/api/study-circles/${studyCircleId}/members/${memberId}`);
            const updatedMembers = members.filter(member => member.member_id !== memberId);
            setMembers(updatedMembers);
            onMembersUpdate(updatedMembers);
            setSuccess('Member removed successfully');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to remove member');
            console.error('Error removing member:', err);
        }
    };

    const handleAddStudent = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setIsAddingStudent(true);
        try {
            const data = {
                [studentSearchType]: studentSearch
            };
            await api.post(`/api/study-circles/${studyCircleId}/add-student`, data);
            setShowAddStudentModal(false);
            setStudentSearch('');
            
            // Refresh members list
            const response = await api.get(`/api/study-circles/${studyCircleId}/members`);
            setMembers(response.data);
            onMembersUpdate(response.data);
            setSuccess('Student added successfully');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to add student');
            console.error('Error adding student:', err);
        } finally {
            setIsAddingStudent(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setIsChangingPassword(true);

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            setIsChangingPassword(false);
            return;
        }

        if (newPassword.length < 6) {
            setError('Password must be at least 6 characters long');
            setIsChangingPassword(false);
            return;
        }

        try {
            await api.put(`/api/users/${selectedMember.user_id}/password`, {
                password: newPassword,
                confirm_password: confirmPassword
            });
            setShowChangePasswordModal(false);
            setNewPassword('');
            setConfirmPassword('');
            setSelectedMember(null);
            setSuccess('Password updated successfully');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update password');
            console.error('Error updating password:', err);
        } finally {
            setIsChangingPassword(false);
        }
    };

    return (
        <div>
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4>Members</h4>
                {user.role === 'admin' && (
                    <button
                        className="btn btn-primary"
                        onClick={() => setShowAddStudentModal(true)}
                    >
                        Add Student
                    </button>
                )}
            </div>

            <div className="table-responsive">
                <table className="table">
                    <thead>
                        <tr>
                            <th>Username</th>
                            <th>Email</th>
                            <th>Role</th>
                            {user.role === 'admin' && <th>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {members.map(member => (
                            <tr key={member.member_id}>
                                <td>{member.username}</td>
                                <td>{member.email}</td>
                                <td>{member.role}</td>
                                {user.role === 'admin' && member.user_id !== user.id && (
                                    <td>
                                        <button
                                            className="btn btn-warning btn-sm me-2"
                                            onClick={() => {
                                                setSelectedMember(member);
                                                setShowChangePasswordModal(true);
                                            }}
                                        >
                                            Change Password
                                        </button>
                                        <button
                                            className="btn btn-danger btn-sm"
                                            onClick={() => handleRemoveMember(member.member_id)}
                                        >
                                            Remove
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Modal show={showAddStudentModal} onHide={() => setShowAddStudentModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Add Student</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleAddStudent}>
                        <Form.Group className="mb-3">
                            <Form.Label>Search by</Form.Label>
                            <Form.Select
                                value={studentSearchType}
                                onChange={(e) => setStudentSearchType(e.target.value)}
                            >
                                <option value="username">Username</option>
                                <option value="email">Email</option>
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Enter {studentSearchType}</Form.Label>
                            <Form.Control
                                type="text"
                                value={studentSearch}
                                onChange={(e) => setStudentSearch(e.target.value)}
                                placeholder={`Enter ${studentSearchType}`}
                                required
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-end">
                            <button type="submit" className="btn btn-primary" disabled={isAddingStudent}>
                                {isAddingStudent ? 'Adding...' : 'Add Student'}
                            </button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            <Modal show={showChangePasswordModal} onHide={() => {
                setShowChangePasswordModal(false);
                setNewPassword('');
                setConfirmPassword('');
                setSelectedMember(null);
            }}>
                <Modal.Header closeButton>
                    <Modal.Title>Change Password for {selectedMember?.username}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleChangePassword}>
                        <Form.Group className="mb-3">
                            <Form.Label>New Password</Form.Label>
                            <Form.Control
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Enter new password"
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Confirm Password</Form.Label>
                            <Form.Control
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Confirm new password"
                                required
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-end">
                            <button type="submit" className="btn btn-primary" disabled={isChangingPassword}>
                                {isChangingPassword ? 'Updating...' : 'Update Password'}
                            </button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default MembersManager; 